import React, { useEffect, useState } from 'react';
import { Col, ConfigProvider, DatePicker, Empty, Row, Select, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../api';
import {
  ApplicationAppointed,
  ApplicationDeclineSvg,
  ApplicationDraftSvg,
  ApplicationInProgress,
  ApplicationReviewed,
  NewApplicationSvg,
} from '../../icons';
import { routerPath } from '../../router/routerPath';
import {
  BlueLayout,
  CustomBigLettersLink,
  CustomButton,
  CustomSelect,
  CustomTableContainer,
  FormBlock,
  FormItem,
  GridItem,
  PagesContainerUI,
} from '../../styles/styled';
import { sortStatus } from '../../utils/sortObject';
import { StatusType } from '../ApplicationPage/types';
import { ExpertisesStatusType, FileRequestType } from '../NewApplicationPage/types';
import {
  RepresentativeExpertiseLineOfTextbooksType,
  // RepresentativeExpertiseLineOfTextbooksType,
  RepresentativeExpertisesResponseType,
  RepresentativeExpertisesType,
} from '../NewRepresentativeApplicationPage/types';
import { RepresentativeSearchType } from './types';

const { Option } = Select;

export const StatusSvgConverter = (id: number) => {
  switch (id) {
    case 1:
      return <NewApplicationSvg />;
    case 2:
      return <ApplicationInProgress />;
    case 3:
      return <ApplicationDeclineSvg />;
    case 4:
      return <ApplicationAppointed />;
    case 5:
      return <ApplicationReviewed />;
    case 6:
      return <ApplicationDraftSvg />;
    default:
      return <p>-</p>;
  }
};

export const RepresentativeApplicationPage = () => {
  const [statusesState, setStatuses] = useState<ExpertisesStatusType>([]);
  const [searchState, setSearchState] = useState<RepresentativeSearchType>({});
  const [showHidden, setHidden] = useState<boolean>(false);
  const navigate = useNavigate();
  const [currentRepresentativeState, setCurrentRepresentative] = useState<RepresentativeExpertisesType>([]);
  const [confirmFilters, setConfirmFilters] = useState<'load' | 'clear' | 'neutral'>('clear');
  const [fileApplicationInstruction, setFileApplicationInstruction] = useState<FileRequestType>();

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<ExpertisesStatusType>(endpoints.getApplicationStatuses());

        setStatuses(response.data.sort(sortStatus));
      } catch (err) {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get(endpoints.getFileBySlug('file_application_instruction'));
        setFileApplicationInstruction(response.data);
      } catch (err) {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (confirmFilters === 'load') {
          const filtersResponse = await api.get<RepresentativeExpertisesResponseType>(
            endpoints.getCurrentRepresentativeExpertisesWithFilters(searchState),
          );
          setConfirmFilters('neutral');
          setCurrentRepresentative(filtersResponse.data.applications);
        } else if (confirmFilters === 'clear') {
          const currentRepresentative = await api.get<RepresentativeExpertisesResponseType>(
            endpoints.getCurrentRepresentativeExpertises(),
          );
          setCurrentRepresentative(currentRepresentative.data.applications);
        }
      } catch (err) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmFilters]);

  const representativeApplicationColumns = [
    {
      title: 'Названия линеек',
      dataIndex: 'line_of_textbooks',
      key: 'title',
      render: (textbooks: RepresentativeExpertiseLineOfTextbooksType) => (
        <p>{textbooks?.length > 0 ? textbooks[0].title : 'Отустствуют линейки'}</p>
      ),
    },
    {
      title: 'Дата подачи заявления',
      dataIndex: 'created_at',
      render: (value: string) => <p>{value.split('T')[0].split('-').join('/')}</p>,
    },
    {
      title: '№ заявления',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Дата регистрации заявления',
      key: 'created_at',
      dataIndex: 'created_at',
      render: (value: string) => <p>{value.split('T')[0].split('-').join('/')}</p>,
    },
    {
      title: 'Регистрационный номер заявления',
      key: 'reg_number',
      dataIndex: 'registration_number',
      render: (regNumber: string | number) => {
        return <p>{regNumber || 'Без номера'}</p>;
      },
    },
    {
      title: 'Кол-во учебников',
      key: 'books_amount',
      dataIndex: 'line_of_textbooks',
      render: (textbooks: RepresentativeExpertiseLineOfTextbooksType) => {
        return <p>{textbooks?.map((el) => el?.textbooks?.length).reduce((el, sum) => sum + el, 0) || '0'}</p>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: StatusType) => StatusSvgConverter(status.id),
    },
  ];

  return (
    <PagesContainerUI>
      <BlueLayout>Заявления на проведение экспертизы учебника</BlueLayout>
      <FormBlock position="relative">
        <Row gutter={[30, 30]}>
          <Col span={12}>
            <FormItem>
              <p>Дата подачи заявления</p>
              <GridItem gap="20px" wrap="nowrap" direction="row">
                <div className="text">с</div>
                <DatePicker
                  format="DD.MM.YYYY"
                  onChange={(value) => {
                    setSearchState({ ...searchState, date_application_from: value?.format('YYYY-MM-DD') });
                  }}
                />
                <div className="text">по</div>
                <DatePicker
                  format="DD.MM.YYYY"
                  onChange={(value) => {
                    setSearchState({ ...searchState, date_application_to: value?.format('YYYY-MM-DD') });
                  }}
                />
              </GridItem>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem>
              <p>Дата регистрации заявления</p>
              <GridItem gap="20px" wrap="nowrap" direction="row">
                <div className="text">с</div>
                <DatePicker
                  format="DD.MM.YYYY"
                  onChange={(value) => {
                    setSearchState({ ...searchState, date_registration_from: value?.format('YYYY-MM-DD') });
                  }}
                />
                <div className="text">по</div>
                <DatePicker
                  format="DD.MM.YYYY"
                  onChange={(value) => {
                    setSearchState({ ...searchState, date_registration_to: value?.format('YYYY-MM-DD') });
                  }}
                />
              </GridItem>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem>
              <div className="text">Статус</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchState({
                    ...searchState,
                    status: parseInt(Object.values(option)[0]),
                  });
                }}
                value={searchState?.status ? statusesState?.find((el) => el.id === searchState.status)?.name : 'Все'}
                showSearch
              >
                <Option key={undefined} value={undefined}>
                  Все
                </Option>
                {statusesState?.map((el) => {
                  return (
                    <Option key={el.id} value={el.name}>
                      {el.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </FormItem>
          </Col>
          <Col span={12} />
          <Col span={12}>
            <GridItem justify="flex-end" wrap="nowrap">
              <CustomButton onClick={() => setConfirmFilters('load')}>Найти</CustomButton>
            </GridItem>
          </Col>
          <Col span={12}>
            <GridItem justify="flex-end">
              <CustomButton
                onClick={() => {
                  setConfirmFilters('clear');
                  setSearchState({});
                }}
                lined
              >
                Очистить
              </CustomButton>
            </GridItem>
          </Col>
        </Row>
      </FormBlock>
      <GridItem justify="space-between" wrap="nowrap" direction="row">
        <CustomBigLettersLink href={fileApplicationInstruction?.file_path} target="_blank" rel="noreferrer">
          инструкция по заполнению заявления
        </CustomBigLettersLink>{' '}
        <GridItem width="auto" direction="row" gap="20px" wrap="nowrap">
          <CustomButton onClick={() => setHidden(!showHidden)} width="300px" light={!showHidden}>
            {!showHidden ? 'Отображать скрытые' : 'Скрыть отображенные'}
          </CustomButton>
          <CustomButton onClick={() => navigate(routerPath.newRepresentativeApplication)} width="300px" lined>
            Подать заявление
          </CustomButton>
        </GridItem>
      </GridItem>
      <CustomTableContainer>
        <ConfigProvider
          renderEmpty={() => (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Экспертизы у данного эксперта не найдены." />
          )}
        >
          <Table
            onRow={(record) => {
              return {
                onClick: () => navigate(routerPath.editRepresentativeApplication + record.id),
              };
            }}
            columns={representativeApplicationColumns}
            dataSource={currentRepresentativeState}
          />
        </ConfigProvider>
      </CustomTableContainer>
    </PagesContainerUI>
  );
};
